import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import {
  Users,
  Search,
  MapPin,
  Mail,
  Building,
  DollarSign,
  ArrowLeft,
  UserCheck,
  UserX
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";

const AdminEmployeesPage = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const token = localStorage.getItem("cdace_token");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/admin/employees`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setEmployees(response.data.employees);
      } catch (error) {
        console.error("Error fetching employees:", error);
        if (error.response?.status === 401) navigate("/admin-login");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token, navigate]);

  const filteredEmployees = employees.filter(e =>
    e.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.department?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.country?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <div className="min-h-screen bg-[#0b1c2d] flex items-center justify-center"><div className="text-[#c9a227]">Loading...</div></div>;
  }

  return (
    <div className="min-h-screen bg-[#0b1c2d] lg:ml-64 p-6" data-testid="admin-employees-page">
      <div className="mb-6">
        <Link to="/admin" className="flex items-center gap-2 text-slate-400 hover:text-white mb-4">
          <ArrowLeft className="w-4 h-4" />Back to Dashboard
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Employees</h1>
            <p className="text-slate-400 mt-1">{employees.length} total employees</p>
          </div>
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input placeholder="Search employees..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9 bg-slate-900/50 border-slate-700" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredEmployees.map((emp) => (
          <Card key={emp.id} className="glass-card hover:border-[#c9a227]/50 transition-colors">
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#c9a227]/20 flex items-center justify-center">
                    <span className="text-[#c9a227] font-bold">{emp.full_name?.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="font-semibold text-white">{emp.full_name}</p>
                    <p className="text-sm text-slate-400">{emp.employee_number}</p>
                  </div>
                </div>
                <Badge className={emp.status === 'active' ? 'status-green' : 'status-yellow'}>
                  {emp.status === 'active' ? <UserCheck className="w-3 h-3 mr-1" /> : <UserX className="w-3 h-3 mr-1" />}
                  {emp.status}
                </Badge>
              </div>
              <div className="space-y-2 text-sm">
                <p className="flex items-center gap-2 text-slate-400"><Building className="w-4 h-4" />{emp.department}</p>
                <p className="flex items-center gap-2 text-slate-400"><MapPin className="w-4 h-4" />{emp.country}</p>
                <p className="flex items-center gap-2 text-slate-400"><Mail className="w-4 h-4" />{emp.email}</p>
                <p className="flex items-center gap-2 text-slate-400"><DollarSign className="w-4 h-4" />${emp.hourly_rate}/hr</p>
              </div>
              <div className="mt-3 pt-3 border-t border-slate-700">
                <Badge className="bg-slate-700 text-slate-300 capitalize">{emp.role?.replace('_', ' ')}</Badge>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminEmployeesPage;
